/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 내비게이션 클래스를 정의한다.
 *
 * @file /src/components/navigation/Navigation.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 30.
 */
import * as parent from '@components/panel/Panel';
import { PanelProvider } from '@base/Viewport';
import { Window } from '@components/window/Window';
import { Form } from '@components/form/Form';
import { Text } from '@components/form/field/text/Text';
import { Button } from '@components/button/Button';
import { Item } from './elements/Item';
import { Folder } from './elements/Folder';
import { Page } from './elements/Page';
import { Ajax } from '@utils/Ajax';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Utils } from '@utils/Utils';
import { Language } from '@utils/Language';
import { Format } from '@utils/Format';
import { UUID } from '@utils/UUID';
import './Navigation.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 항목을 가져올 URL
     */
    url: string;

    /**
     * 아이콘
     */
    icon?: string;

    /**
     * 기본시작경로
     */
    basePath?: string;

    /**
     * 현재경로
     */
    contextPath?: string;

    /**
     * 패널로더
     */
    panelLoader?: (url: string) => Promise<boolean | PanelProvider>;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Navigation extends parent.Panel {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'navigation';

    /**
     * 항목을 가져올 URL
     */
    url: string;

    /**
     * 아이콘
     */
    icon: string;

    /**
     * 기본시작경로
     */
    basePath: string;

    /**
     * 현재경로
     */
    contextPath?: string;

    /**
     * 패널로더
     */
    panelLoader: (url: string) => Promise<boolean | PanelProvider> = null;

    /**
     * 내비게이션 목록
     */
    contexts: Item[] = [];

    /**
     * 제목
     */
    $title: Dom = null;

    /**
     * 툴바
     */
    $tools: Dom = null;

    /**
     * 검색 Dom
     */
    $search: Dom = null;

    /**
     * 내비게이션 패널 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Omit<Properties<Navigation>, 'items'> = null) {
        super(properties);

        this.url = this.properties.url;
        this.icon = this.properties.icon ?? 'mi mi-imodules';
        this.basePath = this.properties?.basePath ?? '/';
        this.contextPath = this.properties.contextPath ?? '/';
        this.panelLoader = this.properties.panelLoader ?? null;
        this.scrollable = 'y';
    }

    /**
     * 툴바 Dom 을 가져온다.
     *
     * @returns 툴바 Dom
     */
    $getTools(): Dom {
        if (this.$tools === null) {
            this.$tools = Html.create('div', { 'data-role': 'tools' });

            const $title = Html.create('h1');
            $title.append(Html.icon(this.icon));
            this.$tools.append($title);

            const $buttons = Html.create('div');

            const $folder = Html.create('button', { type: 'button', 'data-action': 'folder' });
            $folder.on('click', () => {
                new Window({
                    title: Language.get('components.navigation.add_folder'),
                    width: 400,
                    closable: true,
                    modal: true,
                    items: [
                        new Form({
                            layout: 'fit',
                            border: false,
                            items: [
                                new Text({
                                    name: 'title',
                                    flex: true,
                                    placeholder: Language.get('components.navigation.folder_name'),
                                    listeners: {
                                        keyboard: (e, me) => {
                                            if (e.key == 'Enter') {
                                                e.preventDefault();
                                                const window = me.getParent().getParent() as Window;
                                                const button = window.getButtonAt(1);
                                                button.onClick();
                                            }
                                        },
                                    },
                                }),
                            ],
                        }),
                    ],
                    buttons: [
                        new Button({
                            text: Language.get('buttons.cancel'),
                            handler: (button) => {
                                const window = button.getParent().getParent() as Window;
                                window.close();
                            },
                        }),
                        new Button({
                            text: Language.get('buttons.ok'),
                            buttonClass: 'confirm',
                            handler: async (button) => {
                                const window = button.getParent().getParent() as Window;
                                const form = window.getItemAt(0) as Form;
                                const title = (await form.getField('title').getValue()) ?? '';
                                if (title.length > 0) {
                                    const folder = new Folder({
                                        name: UUID.v7(),
                                        title: title,
                                        children: [],
                                    });
                                    folder.setComponent(this);
                                    this.contexts.push(folder);
                                    this.$getContent().append(folder.$getElement());
                                    folder.render();

                                    this.getScroller().updatePosition();

                                    folder.$getElement().scrollTo(true, this.getScroller().$target, { bottom: 20 });

                                    window.close();
                                }
                            },
                        }),
                    ],
                }).show();
            });
            $buttons.append($folder);

            const $config = Html.create('button', { type: 'button', 'data-action': 'config' });
            $config.on('click', async ($button) => {
                if (this.$getContainer().hasClass('config') == true) {
                    $button.disable();
                    const contexts = this.getContexts();
                    const results = await Ajax.post(this.url, {
                        contexts,
                    });

                    if (results.success === true) {
                        this.updateMap();
                    }

                    this.$getContainer().removeClass('config');
                    $button.enable();
                    this.getScroller().setTrackPosition('y', 'var(--aui-component-gap)');
                } else {
                    this.$getContainer().addClass('config');
                    this.getScroller().setTrackPosition('y', 0);
                }

                this.getScroller().updatePosition();
            });
            $buttons.append($config);

            const $toggle = Html.create('button', { type: 'button', 'data-action': 'toggle' });
            $toggle.on('click', () => {
                if (this.$getComponent().hasClass('collapse') == true) {
                    if (this.width !== null) {
                        this.$getComponent().setStyle('width', this.width + 'px');
                    } else if (this.length !== null) {
                        this.$getComponent().setStyle('width', this.length + 'rem');
                    }
                    this.$getComponent().removeClass('collapse');
                    this.storeStatus('collapsed', false);
                    this.getScroller().setHovered(true);
                    this.getScroller().setTrackPosition('y', 'var(--aui-component-gap)');
                } else {
                    this.$getComponent().setStyle('width', null);
                    this.$getComponent().addClass('collapse');
                    this.storeStatus('collapsed', true);
                    this.getScroller().setHovered(false);
                    this.getScroller().setTrackPosition('y', 0);
                }

                this.getScroller().updatePosition();
            });
            $buttons.append($toggle);

            this.$tools.append($buttons);
        }

        return this.$tools;
    }

    /**
     * 검색 DOM 을 가져온다.
     *
     * @returns 검색 DOM
     */
    $getSearch(): Dom {
        if (this.$search === null) {
            this.$search = Html.create('input', { name: 'nav-input', type: 'text', 'autocomplete': 'off' });
            this.$search.setAttr('placeholder', 'Search...');
            this.$search.on('input', ($me) => {
                this.contexts.forEach((item) => {
                    item.search(Format.keycode($me.getValue()));
                });
            });
        }

        return this.$search;
    }

    /**
     * 패널로더를 가져온다.
     *
     * @returns 패널로더
     */
    getPanelLoader(): (url: string) => Promise<boolean | PanelProvider> {
        return this.panelLoader;
    }

    /**
     * 현재 정렬된 내비게이션 목록을 가져온다.
     *
     * @returns 현재 정렬된 내비게이션 목록
     */
    getContexts(): Array<string | { icon: string; title: string; children: string[] }> {
        const contexts = [];
        Html.all('> aui-element[data-type=navigationitem]', this.$getContent()).forEach(($item: Dom) => {
            if ($item.getAttr('data-variant') === 'folder') {
                const folder = {
                    name: $item.getData('name'),
                    title: $item.getData('title'),
                    children: [],
                };

                const $children = Html.get('div[data-role=folderitems]', $item);
                Html.all('aui-element[data-type=navigationitem]', $children).forEach(($child: Dom) => {
                    folder.children.push({ path: $child.getData('path') });
                });

                contexts.push(folder);
            } else {
                contexts.push({ path: $item.getData('path') });
            }
        });

        return contexts;
    }

    /**
     * 내비게이션의 현재 상태를 저장하고, 페이지가 새로고침되었을 때 내비게이션의 마지막 상태를 복원한다.
     *
     * @param name - 상태명
     * @param 상태값
     */
    storeStatus(name: string, value: any): void {
        let navigation = Utils.storage('navigation') ?? {};
        navigation[name] = value;

        Utils.storage('navigation', navigation);
    }

    /**
     * 내비게이션의 마지막 상태를 가져온다.
     *
     * @param name - 상태명
     * @returns 상태값
     */
    getStoredStatus(name: string): object | boolean {
        let navigation = Utils.storage('navigation') ?? {};
        return navigation[name] ?? null;
    }

    /**
     * 내비게이션 기본경로와 내비게이션 항목의 경로를 합친 경로를 가져온다.
     *
     * @param path - 항목경로
     * @returns 전체경로
     */
    getPath(path: string): string {
        return this.basePath.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
    }

    /**
     * 내비게이션 맵을 업데이트한다.
     */
    async updateMap(): Promise<void> {
        const results = await Ajax.get(this.url);

        if (results.success === true) {
            this.contexts = [];
            this.$getContent().empty();

            for (const properties of results.contexts) {
                let item: Item;
                if (properties.children !== undefined) {
                    item = new Folder(properties);
                } else {
                    item = new Page(properties);
                }
                item.setComponent(this);
                this.contexts.push(item as Item);
                this.$getContent().append(item.$getElement());
                item.render();
            }

            this.getScroller().updatePosition();
        }
    }

    /**
     * 헤더를 렌더링한다.
     */
    async renderHeader(): Promise<void> {
        const $tools = this.$getTools();
        const $search = this.$getSearch();
        this.$getHeader().append($tools);
        this.$getHeader().append($search);
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        if (this.$getContainer().isConnected() == false) {
            this.$getComponent().append(this.$getContainer());
        }

        if (this.width !== null) {
            this.$getComponent().setStyle('width', this.width + 'px');
        } else if (this.length !== null) {
            this.$getComponent().setStyle('width', this.length + 'rem');
        }
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        this.$getContainer().append(this.$getHeader());
        this.$getContainer().append(this.$getContent());

        await this.renderHeader();
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();
        await this.updateMap();

        this.getScroller().setHovered(this.getStoredStatus('collapsed') === false);
        if (this.getStoredStatus('collapsed') === false) {
            this.getScroller().setTrackPosition('y', 'var(--aui-component-gap)');
        }

        if (this.getStoredStatus('collapsed') === true) {
            this.$getComponent().setStyle('width', null);
            this.$getComponent().addClass('collapse');
        }
    }
}
